import React, { useEffect } from 'react';
import { KEYS, getIsPrintableCharacter } from '../../common/keyCodes';
import debounce from 'lodash/debounce';
const DELAY_TOLERANCE = 500;
const filterOptions = (options, input) => options.filter((option) => option.value?.toLocaleLowerCase().startsWith(input.toLocaleLowerCase()));
export const useComboboxPatternKeys = ({ options, onSelect, isOpen, setIsOpen, focusedOptionId, setFocusedOptionId, }) => {
    const [input, setInput] = React.useState('');
    const clearInputDebounced = React.useCallback(debounce(() => setInput(''), DELAY_TOLERANCE), [setInput]);
    useEffect(() => () => {
        clearInputDebounced.cancel();
    }, []);
    const matchedOptions = React.useMemo(() => filterOptions(options, input), [options, input]);
    const isValidSelection = (id) => {
        const option = options.find((opt) => opt.id === id);
        return option?.isSelectable && !option.isDisabled;
    };
    const safeSetFocusedOptionId = (id) => {
        if (id !== undefined) {
            setFocusedOptionId(id);
        }
    };
    const open = (e) => {
        setIsOpen(true);
        e.preventDefault();
    };
    const close = (e) => {
        setIsOpen(false);
        e.preventDefault();
    };
    const handleLetters = (key) => {
        if (key === input) {
            const currentMatchIndex = matchedOptions.findIndex((option) => option.id === focusedOptionId);
            const nextMatchIndex = (currentMatchIndex + 1) % matchedOptions.length;
            const nextMatch = matchedOptions[nextMatchIndex];
            safeSetFocusedOptionId(nextMatch.id);
            clearInputDebounced.cancel();
        }
        else {
            const newInput = (input + key).toLocaleLowerCase();
            const newMatchedOptions = filterOptions(options, newInput);
            if (newMatchedOptions.length > 0) {
                safeSetFocusedOptionId(newMatchedOptions[0].id);
            }
            setInput(newInput);
            clearInputDebounced();
        }
    };
    const focusFirstOption = () => {
        safeSetFocusedOptionId(options[0].id);
    };
    const focusLastOption = () => {
        safeSetFocusedOptionId(options[options.length - 1].id);
    };
    const getFocusedOptionIndex = () => focusedOptionId
        ? options.findIndex((option) => option.id === focusedOptionId)
        : -1;
    const focusNextOption = () => {
        const currentIndex = getFocusedOptionIndex();
        for (let index = currentIndex + 1; index < options.length; index++) {
            const nextOption = options[index];
            if (nextOption.divider) {
                continue;
            }
            safeSetFocusedOptionId(nextOption.id);
            break;
        }
    };
    const focusPreviousOption = () => {
        const focusedOptionIndex = getFocusedOptionIndex();
        const currentIndex = focusedOptionIndex === -1 ? options.length : focusedOptionIndex;
        for (let index = currentIndex - 1; index >= 0; index--) {
            const previousOption = options[index];
            if (previousOption.divider) {
                continue;
            }
            safeSetFocusedOptionId(previousOption.id);
            break;
        }
    };
    const focusBackTenOptions = () => {
        // move up 10 options;
        const currentIdx = getFocusedOptionIndex();
        const newIdx = Math.max(currentIdx - 10, 0);
        safeSetFocusedOptionId(options[newIdx].id);
    };
    const focusForwardTenOptions = () => {
        // move down 10 options;
        const currentIdx = getFocusedOptionIndex();
        const newIdx = Math.min(currentIdx + 10, options.length - 1);
        safeSetFocusedOptionId(options[newIdx].id);
    };
    const selectFocused = () => {
        if (focusedOptionId && isValidSelection(focusedOptionId)) {
            onSelect(focusedOptionId);
        }
    };
    const handleListboxKeydown = (event) => {
        switch (event.key) {
            case KEYS.Enter:
            case KEYS.Spacebar:
                selectFocused();
                event.preventDefault();
                break;
            case KEYS.Tab:
                selectFocused();
                break;
            case KEYS.Escape:
                close(event);
                break;
            case KEYS.ArrowDown:
                event.preventDefault();
                focusNextOption();
                break;
            case KEYS.ArrowUp:
                event.preventDefault();
                if (event.altKey) {
                    selectFocused();
                    setIsOpen(false);
                }
                else {
                    focusPreviousOption();
                }
                break;
            case KEYS.Home:
                event.preventDefault();
                focusFirstOption();
                break;
            case KEYS.End:
                event.preventDefault();
                focusLastOption();
                break;
            case KEYS.PageUp:
                event.preventDefault();
                focusBackTenOptions();
                break;
            case KEYS.PageDown:
                event.preventDefault();
                focusForwardTenOptions();
                break;
        }
    };
    const handleComboboxKeydown = (event) => {
        switch (event.key) {
            case KEYS.ArrowUp:
                if (!focusedOptionId) {
                    focusFirstOption();
                }
                open(event);
                break;
            case KEYS.ArrowDown:
            case KEYS.Enter:
            case KEYS.Spacebar:
                open(event);
                break;
            case KEYS.Home:
                focusFirstOption();
                open(event);
                break;
            case KEYS.End:
                focusLastOption();
                open(event);
                break;
            default:
                return;
        }
    };
    const handleKeydown = (event) => {
        const { key } = event;
        if (getIsPrintableCharacter(key) && key !== KEYS.Spacebar) {
            if (!isOpen) {
                open(event);
            }
            handleLetters(key);
            return;
        }
        if (isOpen) {
            handleListboxKeydown(event);
        }
        else {
            handleComboboxKeydown(event);
        }
    };
    return {
        handleKeydown,
    };
};
//# sourceMappingURL=useComboboxPatternKeys.js.map